import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { apiCheckoutStatus, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MAX_ATTEMPTS = 12;
const INTERVAL_MS = 2500;

export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | expired | error
  const [err, setErr] = useState("");
  const attempts = useRef(0);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (!sessionId) { setState("error"); setErr("Missing session_id"); return; }

    const tick = async () => {
      try {
        const data = await apiCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          setErr("Payment status check timed out. If you completed payment, refresh your dashboard.");
          return;
        }
        setTimeout(tick, INTERVAL_MS);
      } catch (e) {
        setState("error");
        setErr(formatApiError(e));
      }
    };
    tick();
  }, [location.search, refresh]);

  return (
    <section className="pt-20 max-w-md mx-auto text-center pb-20" data-testid="payment-success">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="border border-neutral-900 bg-neutral-950 p-8">
        {state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[#00E5FF]" />
            <h1 className="text-2xl font-black text-white">Confirming payment…</h1>
            <p className="text-sm text-neutral-400 mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: "#00FF66" }} />
            <h1 className="text-2xl font-black text-white">You're a member 🎉</h1>
            <p className="text-sm text-neutral-400 mt-2">Membership active — 2 Expert Reviews unlocked for this cycle.</p>
            <button onClick={() => navigate("/dashboard")} data-testid="go-dashboard"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors">
              Go to Dashboard
            </button>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
            <h1 className="text-2xl font-black text-white">Session expired</h1>
            <p className="text-sm text-neutral-400 mt-2">Please try again.</p>
            <button onClick={() => navigate("/membership")} data-testid="retry-checkout"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-neutral-800 text-white font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-900 transition-colors">
              Try again
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-10 h-10 mx-auto mb-3 text-red-400" />
            <h1 className="text-2xl font-black text-white">Something went wrong</h1>
            <p className="text-sm text-neutral-400 mt-2" data-testid="payment-error">{err}</p>
            <button onClick={() => navigate("/dashboard")} data-testid="back-dashboard"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-neutral-800 text-white font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-900 transition-colors">
              Back to Dashboard
            </button>
          </>
        )}
      </motion.div>
    </section>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <section className="pt-20 max-w-md mx-auto text-center pb-20" data-testid="payment-cancel">
      <div className="border border-neutral-900 bg-neutral-950 p-8">
        <XCircle className="w-10 h-10 mx-auto mb-3 text-neutral-500" />
        <h1 className="text-2xl font-black text-white">Payment cancelled</h1>
        <p className="text-sm text-neutral-400 mt-2">You can become a member anytime.</p>
        <button onClick={() => navigate("/membership")} data-testid="cancel-retry"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-6 py-3 hover:bg-neutral-200 transition-colors">
          Back to Membership
        </button>
      </div>
    </section>
  );
}
